// @ts-strict-ignore
import { Injectable, NgZone, ViewContainerRef } from '@angular/core';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMCubeDimensionsDialogComponent } from './lm-cube-dimensions-dialog.component';
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { LMCubePeriodOverrideDialogComponent } from './lm-cube-periodoverride-dialog.component';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AppConstants, FormRequestType, LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { LMCubeDimensionsMappingComponent } from '@modal/cubes/lm-cube-dimensions-mapping.component';
import { KeyedResource } from '@resources/keyed.resource';
import { LMErrorUtilService, LMFormatUtilService, LMUrlUtilService } from '@services/utils';
import { LMCubeRequest, LMDimension, LMDimensionMappingResult, LMReportingBasis } from '@modal/cubes/lm-cubes';
import { LMCubeStructuresComponent } from '@modal/cubes/lm-cube-structures.component';
import { LMLookupComponent } from '@field/lookup';
import { LMSpecService } from '@app/services';
import { LMSelectDialogService, LMSelectResponse } from '@app/components';

@Injectable()
export class LMCubeUtilService {
  constructor(
    private ngZone: NgZone,
    private http: HttpClient,
    private sohoModalDialogService: SohoModalDialogService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmLoggerService: LMLoggerService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmSpecService: LMSpecService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmSelectDialogService: LMSelectDialogService,
    private lmErrorUtilService: LMErrorUtilService,
  ) {
  }

  /**
   * open the dimensions dialog.
   * @param listSpec
   * @param dataViewSet
   * @param placeholder
   * @return
   */
  public openCubeDimensionsDialog(listSpec: LMListSpec, dataViewSet: LMDataViewSet, listState: LMListState, placeholder: ViewContainerRef): Observable<any> {
    return new Observable<any>((observer) => {
      let dlgComponent: LMCubeDimensionsDialogComponent;
      const dialogId = 'DimensionTree';
      const buttons = [
        // TODO: The button default disables the button. A bug in controls?
        // tslint:disable-next-line - shadowed variable e
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            dialog.close();
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.RESET ]),
          text: Soho.Locale.translate(LMDialogButtonKey.RESET),
          click: (e, modal) => {
            dialog.close(dlgComponent.onReset());
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.APPLY ]),
          text: Soho.Locale.translate(LMDialogButtonKey.APPLY),
          validate: false,
          isDefault: true,
          click: (e, modal) => {
            dialog.close(dlgComponent.onApply());
          }
        },
      ];

      const attributes: SohoAttributesArray = [{ name: 'data-automation-id', value: `lm-cube-dimensions-modal` }];

      const dialog: SohoModalDialogRef<LMCubeDimensionsDialogComponent> = this.sohoModalDialogService
        .modal(LMCubeDimensionsDialogComponent, placeholder, { fullsize: 'responsive', attributes } )
        .buttons(buttons)
        .title(Soho.Locale.translate(dialogId))
        .isAlert(false)
        .open();

      dialog.apply((dimDialog: LMCubeDimensionsDialogComponent) => {
        dlgComponent = dimDialog;
        dimDialog.listSpec = listSpec;
        dimDialog.listState = listState;
        dimDialog.dvsLastFixedIndex = dataViewSet.cubeInfo.lastFixedIndex;
        dimDialog.availableDimensions = dataViewSet.cubeInfo.availableDims;
        dimDialog.currentDimensions = dataViewSet.cubeInfo.currentDims;
        dimDialog.dvsShowLevels = dataViewSet.showLevels;
        dimDialog.parentContainer = 'cubeviewer';
      });
      dialog.afterClose(result => {
        observer.next(result);
        observer.complete();
      });
    });
  }

  /**
   * open the Period override dialog.
   * @param listSpec
   * @param dataViewSet
   * @param placeholder
   * @return
   */
  public openCubePeriodOverrideDialog(listSpec: LMListSpec, dataViewSet: LMDataViewSet, listState: LMListState, placeholder: ViewContainerRef): Observable<any> {
    return new Observable<any>((observer) => {
      let dlgComponent: LMCubePeriodOverrideDialogComponent;
      const dialogId = 'PeriodOverride';
      const buttons = [
        // TODO: The button default disables the button. A bug in controls?
        // tslint:disable-next-line - shadowed variable e
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            dialog.close();
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.RESET ]),
          text: Soho.Locale.translate(LMDialogButtonKey.RESET),
          click: (e, modal) => {
            dialog.close(dlgComponent.onReset());
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.APPLY ]),
          text: Soho.Locale.translate(LMDialogButtonKey.APPLY),
          validate: false,
          isDefault: true,
          click: (e, modal) => {
            dialog.close(dlgComponent.onApply());
          }
        },
      ];
      const dialog: SohoModalDialogRef<LMCubePeriodOverrideDialogComponent> = this.sohoModalDialogService
        .modal(LMCubePeriodOverrideDialogComponent, placeholder, { fullsize: 'responsive' })
        .buttons(buttons)
        .title(Soho.Locale.translate(dialogId))
        .isAlert(false)
        .open();

      dialog.apply((periodDialog: LMCubePeriodOverrideDialogComponent) => {
        dlgComponent = periodDialog;
        periodDialog.listSpec = listSpec;
        periodDialog.dataViewSet = dataViewSet;
        periodDialog.listState = listState;
      });
      dialog.afterClose(result => {
        observer.next(result);
        observer.complete();
      });
    });
  }

  public buildCubeStructuresDialog(placeHolder: ViewContainerRef,
    busClass: string,
    dimensionValues?: LMDimensionValues,
    dimensionMappingInput?: Array<LMDimension>,
    reportingBasis?: LMReportingBasis): Observable<LMDimensionMappingResult> {
    const dimensionMappingResult = new Subject<LMDimensionMappingResult>();
    const closeModalWith = (result: LMDimensionMappingResult, modal) => {
      modal.close(true);
      dimensionMappingResult.next(result);
      dimensionMappingResult.complete();
    }

    this.getDimensionMapping(busClass, dimensionValues, dimensionMappingInput, true)
      .subscribe((dimMappingResult: LMDimensionMappingResult) => {
        let cubeStructureComponentRef: LMCubeStructuresComponent;

        const buttons = [
          {
            id: this.lmUniqueIdService.uniqueId([ 'lm-cube-util', 'structures', LMDialogButtonKey.CANCEL ]),
            text: Soho.Locale.translate('Cancel'),
            click: (e, modal) => {
              closeModalWith(undefined, modal);
            }
          },
          {
            id: this.lmUniqueIdService.uniqueId([ 'lm-cube-util', 'showAlert', LMDialogButtonKey.RESET ]),
            text: Soho.Locale.translate('Reset'),
            click: (e, modal) => {
              closeModalWith({ dimensionMapping: [], message: 'reset' }, modal);
            }
          },
          {
            id: this.lmUniqueIdService.uniqueId([ 'lm-cube-util', 'structures', LMDialogButtonKey.OK ]),
            text: Soho.Locale.translate('Ok'),
            click: (e, modal) => {
              const newDimensionMapping = cubeStructureComponentRef.getDimensionMapping();
              const newReportingBasis = cubeStructureComponentRef.getReportingBasis();
              const newPeriodKey = cubeStructureComponentRef.getCubePeriodKey();
              closeModalWith({ dimensionMapping: newDimensionMapping, message: dimMappingResult.message, reportingBasis: newReportingBasis, cubePeriodKey: newPeriodKey}, modal);
            },
            isDefault: true,
          },
        ];

        const dialogTitle = busClass + ' ' + Soho.Locale.translate('Structures');
        this.sohoModalDialogService
          .modal(LMCubeStructuresComponent, placeHolder, { fullsize: 'responsive' })
          .title(dialogTitle)
          .buttons(buttons)
          .open()
          .apply((dialogComp: LMCubeStructuresComponent) => {
            cubeStructureComponentRef = dialogComp;
            cubeStructureComponentRef.cubeBusClass = busClass;
            cubeStructureComponentRef.message = dimMappingResult.message;
            cubeStructureComponentRef.reportingBasis = reportingBasis;
            // deep clone dimension mapping. On ok, the dimension mapping will be updated.
            cubeStructureComponentRef.dimensionMapping = JSON.parse(JSON.stringify(dimMappingResult.dimensionMapping));
          });
      });

    return dimensionMappingResult.asObservable();
  }

  public buildCubeDimensionMappingDialog(placeHolder: ViewContainerRef,
    busClass: string,
    topNodeBaseValues: LMDimensionValues,
    dimensionValues?: LMDimensionValues,
    dimensionMappingInput?: Array<LMDimension>,
    structure = false): Observable<LMDimensionMappingResult> {
    const dimensionMappingResult = new Subject<LMDimensionMappingResult>();
    const closeModalWith = (result: LMDimensionMappingResult, modal) => {
      modal.close(true);
      dimensionMappingResult.next(result);
      dimensionMappingResult.complete();
    }

    this.getDimensionMapping(busClass, dimensionValues, dimensionMappingInput, structure)
      .subscribe((dimMappingResult: LMDimensionMappingResult) => {
        let cubeDimensionsMappingDialogRef: LMCubeDimensionsMappingComponent;

        const buttons = [
          {
            id: this.lmUniqueIdService.uniqueId([ 'lm-cube-util', 'dimensions', LMDialogButtonKey.CANCEL ]),
            text: Soho.Locale.translate('Cancel'),
            click: (e, modal) => {
              closeModalWith(undefined, modal);
            }
          },
          {
            id: this.lmUniqueIdService.uniqueId([ 'lm-cube-util', 'dimensions', LMDialogButtonKey.RESET ]),
            text: Soho.Locale.translate('Reset'),
            click: (e, modal) => {
              closeModalWith({ dimensionMapping: [], message: 'reset' }, modal);
            }
          },
          {
            id: this.lmUniqueIdService.uniqueId([ 'lm-cube-util', 'dimensions', LMDialogButtonKey.OK ]),
            text: Soho.Locale.translate('Ok'),
            click: (e, modal) => {
              const newDimensionMapping = cubeDimensionsMappingDialogRef.getDimensionMapping();
              closeModalWith({ dimensionMapping: newDimensionMapping, message: dimMappingResult.message }, modal);
            },
            isDefault: true,
          },
        ];

        const dialogTitle = busClass + ' ' + Soho.Locale.translate('Dimensions');
        this.sohoModalDialogService
          .modal(LMCubeDimensionsMappingComponent, placeHolder, { fullsize: 'responsive' })
          .title(dialogTitle)
          .buttons(buttons)
          .open()
          .apply((dialogComp: LMCubeDimensionsMappingComponent) => {
            cubeDimensionsMappingDialogRef = dialogComp;
            cubeDimensionsMappingDialogRef.cubeBusClass = busClass;
            cubeDimensionsMappingDialogRef.message = dimMappingResult.message;
            cubeDimensionsMappingDialogRef.displayHelpText = true;
            // deep clone dimension mapping. On ok, the dimension mapping will be updated.
            cubeDimensionsMappingDialogRef.dimensionMapping = JSON.parse(JSON.stringify(dimMappingResult.dimensionMapping));
            cubeDimensionsMappingDialogRef.topNodeBaseValues = topNodeBaseValues;
          });
      });

    return dimensionMappingResult.asObservable();
  }
  public getDimensionMapping(busClass: string, dimensionValues?: LMDimensionValues, dimensionMapping?: Array<LMDimension>, structure = false): Observable<LMDimensionMappingResult> {
    if (!dimensionMapping || dimensionMapping.length === 0) {
      const cubeDimMap = Object.assign(
        { requestType: FormRequestType.CubeDimensionMapping },
        { busClass },
        { dimensionValues },
        { structure });
      return this.executeFormRequest(this.getRequestUrl(busClass), cubeDimMap);
    }
    return of({ dimensionMapping, message: 'cache' });
  }

  public getRequestUrl(busClass: string): string {
    const formResource = new KeyedResource({ resourceType: 'form', resourceId: busClass });
    return this.lmUrlUtilService.buildUrl({ resource: formResource });
  }

  public getSelectListUrl(dimension: LMDimension): string {
    if (dimension.type !== 'keyfield') {
      return undefined;
    }
    const listResource = new KeyedResource({ resourceType: 'list', resourceId: dimension.relatedBusClass + '.' + dimension.uilist });
    const selectResource = new KeyedResource({ resourceType: 'select', resourceId: dimension.relatedBusClass + '.' + dimension.selectField });
    const params: any = { output: 'spec', type: 'select' };
    return this.lmUrlUtilService.buildUrl({ resource: listResource, contextResource: selectResource, params });
  }

  public openPeriodDialogForPeriodDimension(placeHolder: ViewContainerRef, interval: string, currentPeriod: string): Observable<string> {
    const periodKey = new Subject<string>();
    const closeModalWith = (result: string, modal) => {
      modal.close(true);
      periodKey.next(result);
      periodKey.complete();
    };

    let dlgComponent: LMCubePeriodOverrideDialogComponent;
    const dialogId = 'CurrentPeriod';
    const buttons = [
      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: (e, modal) => {
          dialog.close();
        }
      },

      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.APPLY ]),
        text: Soho.Locale.translate(LMDialogButtonKey.APPLY),
        validate: false,
        isDefault: true,
        click: (e, modal) => {
          const periodKeyStr = dlgComponent.getCubeCellRelPeriodKey();
          closeModalWith(periodKeyStr, modal);
        }
      },
    ];
    const dialog: SohoModalDialogRef<LMCubePeriodOverrideDialogComponent> = this.sohoModalDialogService
      .modal(LMCubePeriodOverrideDialogComponent, placeHolder, { fullsize: 'responsive' })
      .buttons(buttons)
      .title(Soho.Locale.translate(dialogId))
      .isAlert(false)
      .open();

    dialog.apply((periodDialog: LMCubePeriodOverrideDialogComponent) => {
      dlgComponent = periodDialog;
      dlgComponent.cubeInterval = interval;
      dlgComponent.cubeCurrentPeriod = currentPeriod;
    });
    return periodKey.asObservable();
  }

  public getNewTopNodeValues(cubeBusClass: string, cubeListName: string, ctxValue: string): Observable<any> {
    const newTopNodes = { requestType: FormRequestType.CubeGetTopNodesForContext,
      busClass: cubeBusClass,
      cubeListName,
      ctxValue };
    return this.executeFormRequest(this.getRequestUrl(cubeBusClass), newTopNodes);
  }

  private executeFormRequest(url: string, cubeRequest: LMCubeRequest): Observable<any> {
    return this.http
      .post(url, cubeRequest).pipe(
        catchError(error => {
          this.lmLoggerService.error(error);
          return throwError(error || 'Server error');
        }));
  }

  public cubeSearchOpenSelectListDialog(dimension: LMDimension, topNodeBaseValue: string, field: LMLookupComponent, searchFormDataView: LMDataView) {
    const selectListUrl = (dimension) ? this.getSelectListUrl(dimension)
      : this.lmUrlUtilService.buildSelectListUrl(field.model.formUrlInfo, field.layoutObject.selectListInfo);
    this.lmSpecService.getListSpec(selectListUrl).subscribe((listSpecResponse: LMListSpecResponse) => {
      this.lmLoggerService.log([ 'LMCubeUtilService', 'getListSpec() completed', listSpecResponse ]);
      const listUrlInfo = listSpecResponse.listUrlInfo;
      const listSpec = listSpecResponse.listSpec;
      const sourceField = (dimension) ? dimension.id : field.specField.selectFieldId || field.bindId;
      // If the list spec has search form, it is either displayed initially or the user will click on search icon to open up.
      // In either case, do not set the height of the modal. The search form and the list together should be in auto height.
      // Also in the case of tree view, do not want to set the modal with overflow:hidden. As the nodes are expanded, we should be able to scroll.
      const className = (listSpec.busListSpec.searchForm === undefined && !listSpec.busListSpec.implementsTreeView) ? 'lm-lookup-modal' : '';

      const fieldHasNoVariables = (!field.specField.label.variables || field.specField.label.variables.length === 0);
      const labelValue = (field.specField.label.resourceValue !== ' ' || !fieldHasNoVariables)
        ? this.lmFormatUtilService.formatMessageRule(field.specField.label, field.model, field.specFields)
        : '';
      const title = (labelValue !== '')
        ? this.lmFormatUtilService.formatMessage(Soho.Locale.translate('SelectTitle'), labelValue)
        : Soho.Locale.translate('Select');

      // If a topDimensionValue is set, use it to intialize the search form dataview
      if (dimension && topNodeBaseValue) {
        const keyNames = dimension.relBusClassKeyNames.split('/');
        const keyValues = topNodeBaseValue.split('/');
        if (keyNames.length > 0) {
          keyNames.forEach((key, i) => {
            if (searchFormDataView.fields[key]) {
              searchFormDataView.fields[key].value = keyValues[i];
            } else {
              searchFormDataView.fields[key] = { value: keyValues[i] };
            }
          });
        }
      }
      const selectRequestData: SelectRequestData = {
        viewType: field.model.formSpec.selectViewType ? field.model.formSpec.selectViewType : 'form',
        viewName: field.model.formSpec.busFormSpec.name,
        dataView: searchFormDataView,
        allowMultiSelections: false,
        onSearchForm: true,
        displayTopNodeAndChildren: (dimension && topNodeBaseValue !== undefined),
      };
      this.lmSelectDialogService.showSelectListDialog({
        listUrlInfo,
        listSpec,
        title,
        sourceField,
        className,
        selectRequestData
      }).pipe(
        first()) // causes the subscription to complete after its messaged the first time
        .subscribe((selectResponse: LMSelectResponse) => {
          this.lmLoggerService.log([ 'LMCubeUtilService', 'cubeSearchOpenSelectListDialog()', selectResponse ]);
          if (selectResponse?.status === AppConstants.STATUS_COMPLETED && selectResponse.dataViews) {
            const selectedDataView: LMDataView = selectResponse.dataViews[0];
            this.applyValue(dimension, searchFormDataView.busClassName, selectedDataView).subscribe((response: any) => {
              if (response.status && response.status === AppConstants.STATUS_SYSTEM_ERROR) {
                this.lmErrorUtilService.handleError(response.message, response.details, response.status);
                return;
              }

              const resDataView: LMDataView = response;
              const lookupValue = resDataView.fields[sourceField].value as string;
              if (dimension) {
                dimension.value = lookupValue;
              }
              field.modelField.value = lookupValue;
              //TODO: When multiselect is turned on, set the model field value and entered filter value using the delimiter.

              // sohoLookupComponent requires a data property on each array element
              const selectedData: Array<any> = [ { data: resDataView } ] ;
              this.ngZone.runOutsideAngular(() => {
                field.sohoLookup.setValue(selectedData);
              });
            });
          } else if (selectResponse?.status === AppConstants.STATUS_CANCEL) {
            this.ngZone.runOutsideAngular(() => {
              field?.inputElement?.nativeElement?.focus();
            });
          }
        });
    },
    (error: any) => {
      this.lmLoggerService.log([ 'LMCubeUtilService cubeSearchOpenSelectListDialog', error ]);
    });
  }

  private applyValue(dimension: LMDimension, busClassName: string, dataView: LMDataView): Observable<any> {
    // getFields call will return the ontological context values along with the selected key for a dimension
    if (dimension) {
      const getFieldsUrl = this.getRequestUrl(busClassName);
      const cubeCellRequest = Object.assign(
        { requestType: FormRequestType.CubeRelGetSelectValue },
        { busClass: busClassName },
        { dimension: dimension.id },
        { selectResourceId: dataView.resourceId + '.' + dimension.uilist },
        { selectDataView: dataView },
      );
      return this.http
        .post(getFieldsUrl, cubeCellRequest).pipe(
          catchError(error => {
            this.lmLoggerService.error(error);
            return throwError(error || 'Server error');
          }));
    } else {
      return of(dataView);
    }
  }
}
